import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

export function UnderwaterTransition() {
  const containerRef = useRef(null);
  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Distortion effect as we pass through the surface - reduced for performance
  const distortionOpacity = useTransform(scrollYProgress, [0, 0.4, 0.7, 1], [0, 0.5, 0.2, 0]);
  const refractionBlur = useTransform(scrollYProgress, [0, 0.4, 1], [0, 3, 0]);

  // Gradual fade overlay that helps surface elements blend away
  const surfaceFadeOverlay = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    ['rgba(13,26,45,0)', 'rgba(13,26,45,0.6)', 'rgba(13,26,45,0.9)']
  );

  // Color shift from surface blue to deeper underwater blue
  const colorOverlay = useTransform(
    scrollYProgress,
    [0, 0.6, 1],
    ['rgba(26,58,82,0.2)', 'rgba(13,26,45,0.5)', 'rgba(9,20,34,0.7)']
  );

  // Faint light rays from above (only visible near top of transition)
  const lightRaysOpacity = useTransform(scrollYProgress, [0, 0.35, 0.7, 1], [0.25, 0.15, 0.05, 0]);

  return (
    <div ref={containerRef} className="relative h-[240px] pointer-events-none">
      {/* Surface fade overlay - helps water surface elements blend away */}
      <motion.div
        className="absolute inset-0 z-5"
        style={{
          background: surfaceFadeOverlay,
        }}
      />

      {/* Refractive distortion overlay */}
      <motion.div
        className="absolute inset-0 z-10"
        style={{
          opacity: distortionOpacity,
          backdropFilter: prefersReducedMotion ? 'none' : `blur(${refractionBlur}px)`,
        }}
      >
        {/* Subtle horizontal distortion lines (like looking through water surface) */}
        {!prefersReducedMotion && (
          <div className="absolute inset-0 opacity-30">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent"
                style={{
                  top: `${i * 16}%`,
                }}
                animate={{
                  scaleX: [1, 1.08, 1],
                  opacity: [0.15, 0.3, 0.15],
                }}
                transition={{
                  duration: 3.5,
                  delay: i * 0.25,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Color temperature shift */}
      <motion.div
        className="absolute inset-0 z-8"
        style={{
          background: colorOverlay,
        }}
      />

      {/* Faint light rays from surface above */}
      <motion.div
        className="absolute inset-0 overflow-hidden"
        style={{ opacity: lightRaysOpacity }}
      >
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="absolute top-0 w-[2px] h-full opacity-10"
            style={{
              left: `${20 + i * 20}%`,
              background: 'linear-gradient(to bottom, rgba(240,184,85,0.3) 0%, transparent 60%)',
              transform: `rotate(${-8 + i * 5}deg)`,
              transformOrigin: 'top',
            }}
          />
        ))}
      </motion.div>

      {/* Underwater haze beginning */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/20"
        style={{
          opacity: useTransform(scrollYProgress, [0, 1], [0, 1]),
        }}
      />
    </div>
  );
}
